import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** A single selectable option for the {@link OptionToggleComponent}. */
export interface OptionToggleItem {
  /** Text displayed inside the segment. */
  label: string;
  /** Value emitted/bound when this segment is selected. */
  value: any;
}

/**
 * A reusable segmented toggle that behaves like a radio group (exactly one option selected
 * at a time) but looks and animates like a slider switch. A sliding "thumb" moves behind the
 * selected segment whenever the selection changes.
 *
 * Implements {@link ControlValueAccessor} so it can be driven with `[(ngModel)]` or reactive
 * forms, and also emits {@link selectionChange} for side-effects on selection.
 *
 * Labels are rendered as plain text by default. Set {@link useHtmlLabel} to `true` to render
 * each label as HTML (e.g. to embed a `multiply-symbol` span).
 *
 * @example
 * ```html
 * <rms-option-toggle
 *   [options]="[{ label: 'M×', value: false }, { label: '%', value: true }]"
 *   [(ngModel)]="showPercent"
 *   (selectionChange)="onToggle()">
 * </rms-option-toggle>
 * ```
 */
@Component({
  selector: 'rms-option-toggle',
  templateUrl: './option-toggle.component.html',
  styleUrls: ['./option-toggle.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OptionToggleComponent),
      multi: true
    }
  ]
})
export class OptionToggleComponent implements ControlValueAccessor {
  @Input() options: Array<OptionToggleItem> = [];
  @Input() disabled = false;
  /** When true, each option's label is rendered as HTML rather than plain text. */
  @Input() useHtmlLabel = false;
  /**
   * When true, the sliding thumb gains an emphasized (tinted + hairline) treatment whenever the
   * current value differs from {@link defaultValue}. Opt-in so existing consumers stay plain.
   */
  @Input() emphasizeNonDefault = false;
  /** The value considered "default"; the thumb is only emphasized when {@link value} differs from it. */
  @Input() defaultValue: any;
  @Output() selectionChange = new EventEmitter<any>();

  public value: any;

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  /** Index of the currently selected option, used to position the sliding thumb. */
  public get selectedIndex(): number {
    return this.options?.findIndex(option => option.value === this.value) ?? -1;
  }

  /** Selects an option, propagating the change through ngModel/forms and emitting selectionChange. */
  public selectOption(option: OptionToggleItem): void {
    if (this.disabled || option.value === this.value) {
      return;
    }
    this.value = option.value;
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
